import { API_BASE_URL, AUTH_STORAGE_KEYS } from './config';

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  timestamp?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

export interface PaginatedApiEnvelope<T> extends ApiEnvelope<T[]> {
  pagination?: PaginationMeta;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: ApiEnvelope<unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type ApiRequestOptions = {
  /** When false, do not attach Bearer token (public endpoints e.g. domain verify). Default true. */
  auth?: boolean;
};

function resolveAuthHeader(auth = true): Record<string, string> {
  if (!auth) return {};
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchWithSessionRetry(
  url: string,
  init: RequestInit,
  auth = true,
): Promise<Response> {
  let response = await fetch(url, init);
  if (!auth) return response;
  if (response.status !== 401 && response.status !== 403) return response;

  let code: string | undefined;
  try {
    const peek = (await response.clone().json()) as ApiEnvelope<unknown>;
    code = peek?.code;
  } catch {
    code = undefined;
  }
  if (code === 'SESSION_REVOKED') {
    const { hardLogout } = await import('@/lib/auth/session-end');
    hardLogout('session-ended');
    return response;
  }
  if (response.status !== 401 && code !== 'SESSION_STALE') return response;

  const { refreshAccessToken } = await import('./auth-client');
  const newToken = await refreshAccessToken();
  if (!newToken) {
    const { hardLogout } = await import('@/lib/auth/session-end');
    hardLogout('session-ended');
    return response;
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${newToken}`);
  return fetch(url, { ...init, headers });
}

/** Unwrap API envelope data or throw when the gateway returned success without a body. */
export function requireApiData<T>(data: T | undefined, message: string): T {
  if (data === undefined) {
    throw new ApiError(message, 500);
  }
  return data;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string>,
  options?: ApiRequestOptions,
): Promise<ApiEnvelope<T>> {
  const url = new URL(path.startsWith('http') ? path : `${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...resolveAuthHeader(options?.auth !== false),
  };

  const response = await fetchWithSessionRetry(
    url.toString(),
    {
      method: 'GET',
      headers,
      credentials: 'include',
    },
    options?.auth !== false,
  );

  let body: ApiEnvelope<T> | undefined;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    body = undefined;
  }

  if (!response.ok || body?.success === false) {
    throw new ApiError(
      body?.error || body?.message || `Request failed (${response.status})`,
      response.status,
      body,
    );
  }

  return body ?? { success: true };
}

function authHeaders(jsonBody: boolean): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (jsonBody) {
    headers['Content-Type'] = 'application/json';
  }
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T> | undefined> {
  if (response.status === 204 || response.status === 205) {
    return undefined;
  }
  const text = await response.text();
  if (!text.trim()) {
    return undefined;
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return undefined;
  }
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<ApiEnvelope<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const response = await fetchWithSessionRetry(url, {
    method: 'POST',
    headers: { ...authHeaders(true), ...extraHeaders },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope as ApiEnvelope<unknown> | undefined,
    );
  }

  return envelope ?? { success: true };
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<ApiEnvelope<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const response = await fetchWithSessionRetry(url, {
    method: 'PATCH',
    headers: { ...authHeaders(true), ...extraHeaders },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope as ApiEnvelope<unknown> | undefined,
    );
  }

  return envelope ?? { success: true };
}

export async function apiPut<T>(
  path: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<ApiEnvelope<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const response = await fetchWithSessionRetry(url, {
    method: 'PUT',
    headers: { ...authHeaders(true), ...extraHeaders },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope as ApiEnvelope<unknown> | undefined,
    );
  }

  return envelope ?? { success: true };
}

export async function apiPostFormData<T>(
  path: string,
  formData: FormData,
): Promise<ApiEnvelope<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetchWithSessionRetry(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope as ApiEnvelope<unknown> | undefined,
    );
  }

  return envelope ?? { success: true };
}

export async function apiUploadFormData<T>(
  path: string,
  formData: FormData,
): Promise<ApiEnvelope<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetchWithSessionRetry(url, {
    method: 'PATCH',
    headers,
    credentials: 'include',
    body: formData,
  });

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope as ApiEnvelope<unknown> | undefined,
    );
  }

  return envelope ?? { success: true };
}

export async function apiDelete<T = unknown>(
  path: string,
  extraHeaders?: Record<string, string>,
): Promise<ApiEnvelope<T> | undefined> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const response = await fetchWithSessionRetry(url, {
    method: 'DELETE',
    headers: { ...authHeaders(false), ...extraHeaders },
    credentials: 'include',
  });

  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  const envelope = await parseEnvelope<T>(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError(
      envelope?.error || envelope?.message || `Request failed (${response.status})`,
      response.status,
      envelope,
    );
  }

  return envelope;
}

export async function apiGetPaginated<T>(
  path: string,
  params?: Record<string, string>,
): Promise<{ data: T[]; pagination: PaginationMeta }> {
  const body = (await apiGet<unknown>(path, params)) as PaginatedApiEnvelope<unknown>;
  const payload = body.data;
  const defaultMeta = (items: T[]): PaginationMeta => ({
    page: 1,
    limit: items.length,
    total: items.length,
    totalPages: 1,
  });

  // HRM CRUD format: { data: { items: T[], meta: PaginationMeta } }
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    'items' in payload
  ) {
    const raw = payload as { items?: T[]; meta?: PaginationMeta };
    const items = Array.isArray(raw.items) ? raw.items : [];
    return {
      data: items,
      pagination: raw.meta ?? defaultMeta(items),
    };
  }

  // Standard paginated envelope: { data: T[], pagination }
  if (Array.isArray(payload)) {
    return {
      data: payload as T[],
      pagination: body.pagination ?? defaultMeta(payload as T[]),
    };
  }

  return { data: [], pagination: { page: 1, limit: 0, total: 0, totalPages: 1 } };
}
